// home.jsx — Home: what to do today, how you're doing, what to fix.
//
// Everything shown is computed from the user's own data (analytics.js) — no
// placeholder numbers. Any workout can be started any day; a scheduled day
// is a suggestion, not a gate.

import React from 'react';
import { simpleDefault } from './glossary.js';
import { LLSheet } from './sheets.jsx';
import { fmtWeight, unitLabel, useApp } from './store.jsx';
import { unlockAudioContext } from './timers.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { localDateStr, prescriptionFor, exerciseNameFor } from './utils.jsx';
import { readiness, deloadAdvice, exerciseHistory, plateauFor, weekStart, weeklyStreak, completedSessions } from './analytics.js';
import { aiEnabled, useAIStatus } from './ai.js';
import { WeeklyReviewCard } from './weekly_review.jsx';
import { isStaleSession, staleEndAt } from './session_model.js';
import { backupNudgeDue, dismissBackupNudge } from './sync.js';
import { nextWorkout, missingTMs, skipProgramDay } from './progression.js';
import { trainedVolumeReport, programWeeklySets, plannedDeload, prsThisWeek } from './insight_rules.js';
import { fmtMinutes, fmtCompact, fmtShortDate } from './format.js';
import { PageHeader } from './phone.jsx';
import { GenerateWorkoutSheet, BuildWorkoutCard } from './generate_sheet.jsx';

const DAY = 86400000;
const MON_FIRST = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.1, color: LL.fgDim, textTransform: 'uppercase' };
const card = { background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md };
const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const minsLabel = fmtMinutes;
const clock = (sec) => `${Math.floor(sec / 60)}:${String(Math.max(0, Math.floor(sec % 60))).padStart(2, '0')}`;

function estMinutes(day, program) {
  if (!day) return 0;
  const secs = day.exercises.reduce((a, pe) => {
    const rx = prescriptionFor(program, pe);
    const sets = rx.sets ?? pe.sets ?? 3;
    return a + sets * (45 + (rx.rest ?? pe.rest ?? 120));
  }, 0);
  return Math.max(10, Math.round(secs / 60 / 5) * 5);
}

function relDay(ts) {
  const d = Math.floor((weekStartDay(Date.now()) - weekStartDay(ts)) / DAY);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return fmtShortDate(ts);
}
function weekStartDay(ts) { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); }

// Top insights, most actionable first. Each: { tone, title, body, action: { label, route, params } }
function useInsights(state, simple = false) {
  return React.useMemo(() => {
    const out = [];
    const done = completedSessions(state);
    if (done.length < 2) return out;
    const program = state.programs.find(p => p.id === state.activeProgramId) || null;
    const dl = deloadAdvice(state);
    // The program's own deload outranks generic advice (never "consider a deload" in W3 of a plan whose W4 is one).
    const planned = plannedDeload(state, program);
    if (dl?.recommend && planned) {
      if (planned.inWeeks > 0) out.push({ tone: 'info', icon: 'flame', title: `Your ${simple ? 'lighter week' : 'deload'} is ${planned.inWeeks === 1 ? 'next week' : `in ${planned.inWeeks} weeks`}`, body: `${dl.reasons.join(' ')} ${program.name} has a planned ${simple ? 'lighter week' : 'deload'} (${planned.label}), so keep pushing until then.`, action: null });
    } else if (dl?.recommend) {
      out.push({ tone: 'warn', icon: 'flame', title: 'Time for a lighter week', body: `${dl.reasons.join(' ')} ${dl.prescription}`, action: { label: simple ? 'Start a lighter week' : 'Start deload week', deload: true } });
    }
    // Plateaus on lifts trained in the last 4 weeks, most-trained first
    // Only lifts in the active program (or any recent lift when there is no program).
    const inProgram = program ? new Set(program.days.flatMap(d => d.exercises.map(e => e.exerciseId))) : null;
    const recentIds = [...new Set(done.filter(s => s.startedAt > Date.now() - 28 * DAY).flatMap(s => (s.sets || []).map(st => st.exerciseId)))].filter(id => !inProgram || inProgram.has(id));
    const stalled = recentIds.map(id => ({ id, h: exerciseHistory(state, id) }))
      .map(x => ({ ...x, p: plateauFor(x.h) })).filter(x => x.p).sort((a, b) => b.h.length - a.h.length);
    if (stalled.length) {
      const s = stalled[0];
      const name = exerciseNameFor(state, s.id);
      const u = unitLabel(state.prefs.units);
      // Bodyweight lifts: e1RM includes body weight, so show sets as "BW+30×7", not a bare 1RM.
      const bwk = s.h[s.h.length - 1]?.bw != null;
      const tops = s.h.slice(-3).map(x => `${bwk ? (x.bestW > 0 ? `BW+${fmtWeight(x.bestW, state.prefs.units)}` : 'BW') : fmtWeight(x.bestW, state.prefs.units)}×${x.bestReps}`).join(', ');
      const ai = aiEnabled();
      out.push({ tone: 'warn', icon: 'plateau', title: `${name} has stalled`,
        body: `${simple ? 'Your estimated max hasn\'t gone up' : 'No e1RM gain'} in your last ${s.p.exposures} sessions${bwk ? '' : ` (best ${fmtWeight(s.p.priorBest, state.prefs.units)} ${u})`}.${tops ? ` Last top sets: ${tops}.` : ''}${ai ? '' : ' Try 1–2 lighter weeks, add a set, or swap to a close variation.'}${stalled.length > 1 ? ` ${stalled.length - 1} other lift${stalled.length > 2 ? 's' : ''} too.` : ''}`,
        action: ai ? { label: 'Ask why', route: 'ai-coach', params: { ctx: { kind: 'stall', exerciseId: s.id, label: name } } } : { label: 'See its history', route: 'exercise', params: { exerciseId: s.id } } });
    }
    // Under-trained muscles (4-week average below MEV)
    // Needs 2+ complete weeks of history, and never for a muscle the program already plans enough sets for.
    const plannedSets = programWeeklySets(state, program);
    const low = trainedVolumeReport(state).filter(v => v.status === 'low' && v.mev > 0 && v.weeksTrained >= 2 && !((plannedSets[v.muscle] || 0) >= v.mev))
      .sort((a, b) => (a.avg4 - a.mev) - (b.avg4 - b.mev));
    if (low.length) {
      const v = low[0];
      const avg = Math.round(v.avg4 * 10) / 10;
      out.push({ tone: 'info', icon: 'barsDown', title: simple ? `${v.muscle}: not enough sets yet` : `${v.muscle} volume is low`, body: `${avg} hard sets${simple ? ' a week' : '/week'} on average — about ${v.mev}${simple ? ' is enough to grow' : '+ is the minimum for growth'}.${low.length > 1 ? ` Also low: ${low.slice(1, 3).map(x => x.muscle).join(', ')}.` : ''}${aiEnabled() ? '' : ` Add ${Math.max(1, Math.ceil(v.mev - v.avg4))} set${Math.ceil(v.mev - v.avg4) === 1 ? '' : 's'} a week in your program.`}`,
        action: aiEnabled() ? { label: 'Fix my program', route: 'ai-coach', params: { ctx: { kind: 'muscle', muscle: v.muscle } } } : { label: 'Edit program', route: 'programs' } });
    }
    // PR momentum this week
    const prs = prsThisWeek(state); // first-ever logs are baselines, not PRs
    if (prs >= 2) out.push({ tone: 'good', icon: 'trophy', title: `${prs} PRs this week`, body: 'Whatever you are doing is working — keep the same recovery habits.', action: { label: 'See progress', route: 'progress' } });
    return out.slice(0, 3);
  }, [state.sessions, state.prs, state.exercises, state.prefs.units, state.programs, state.activeProgramId, simple]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Responsive dashboard grid shared by Home and Progress. Phone (< 900px): a
// single column — .ll-col wrappers dissolve (display: contents) and sections
// keep their phone order via `order`. Desktop: centered, two columns.
export function DashStyles() {
  return (
    <style>{`
      .ll-dash { display: flex; flex-direction: column; }
      .ll-dash > .ll-col { display: contents; }
      @media (min-width: 900px) {
        .ll-wrap { max-width: 1180px; margin: 0 auto; padding: 8px 16px 0; box-sizing: border-box; }
        .ll-dash { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(0, 1fr); column-gap: 8px; align-items: start; }
        .ll-dash > .ll-col { display: flex; flex-direction: column; min-width: 0; }
      }
    `}</style>
  );
}

export function HomeScreen() {
  const { state, setRoute, actions, toast } = useApp();
  const aiMode = useAIStatus().mode;
  const units = state.prefs.units;
  const program = state.programs.find(p => p.id === state.activeProgramId) || state.programs[0] || null;
  const [daySheet, setDaySheet] = React.useState(null); // { day, date }
  const [guard, setGuard] = React.useState(null);       // { day } when another workout is live
  const [genOpen, setGenOpen] = React.useState(false);  // "Build a workout" sheet
  const [deloadAsk, setDeloadAsk] = React.useState(null); // insight asking to start a deload week
  const simple = simpleDefault(state);
  const insights = useInsights(state, simple);
  const ready = React.useMemo(() => readiness(state), [state.sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  const now = new Date();
  const todayStr = localDateStr(now.getTime());
  const done = completedSessions(state);
  const live = state.sessions.find(s => s.id === state.activeSessionId && !s.completedAt) || null;
  const stale = isStaleSession(live);
  const [nudge, setNudge] = React.useState(() => { try { return backupNudgeDue(state); } catch { return false; } });

  const go = (name, params) => setRoute(name, params);
  const startDay = (day) => {
    try { unlockAudioContext(); } catch {}
    // A live session from another day — or a stale one from an earlier date,
    // even for the same day — must be dealt with before starting.
    if (live && (stale || live.dayId !== day.id)) { setGuard({ day, stale }); return; }
    setDaySheet(null);
    go('active-workout', { dayId: day.id });
  };
  const startEmpty = () => {
    try { unlockAudioContext(); } catch {}
    if (live && stale) { setGuard({ day: null, stale: true }); return; }
    if (live) { go('active-workout', { dayId: live.dayId || undefined }); return; }
    go('active-workout', { freestyle: 'Workout' });
  };

  // One engine for "what's next" (progression.nextWorkout): rotation order, week
  // label, skip and not-done badges. A weekday is only a suggestion of *when*.
  const nx = program ? nextWorkout(state, program.id) : null;
  const nextDay = nx?.day || null;
  const doneIds = new Set(nx ? nx.doneDayIds : []);
  const missedIds = new Set(nx ? nx.missedDays.map(m => m.dayId) : []);
  const weekText = !nx ? '' : nx.hasWeeks
    ? `${nx.weekLabel}${nx.deload && !/deload/i.test(nx.weekLabel) ? ' · deload' : ''}`
    : `Week ${nx.weekIndex + 1}${program.weeks ? ` of ${program.weeks}` : ''}`;
  const doneNames = nx ? nx.doneDayIds.map(id => program.days.find(d => d.id === id)?.name).filter(Boolean) : [];
  const joinNames = (a) => (a.length <= 2 ? a.join(' and ') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
  const rotationLine = nx ? [
    nx.plannedFor && !nx.today ? `Planned for ${nx.plannedFor}` : null,
    doneNames.length ? `${joinNames(doneNames)} done this week` : null,
    nx.missedDays.length ? `last week skipped ${joinNames(nx.missedDays.map(m => m.name))}` : null,
  ].filter(Boolean).join(' · ') : '';
  const restDay = !!(nextDay && nx.inDays != null && nx.inDays > 0);

  // Week model (Mon-first). Planned weekdays are markers only; a day is "done"
  // when a workout was logged on that date, and never flagged missed by weekday.
  const ws = weekStart();
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const weekDays = MON_FIRST.map((dn, i) => {
    const ts = ws + i * DAY;
    const dateStr = localDateStr(ts);
    const day = program?.days.find(d => d.day === dn) || null;
    const sessionsOnDay = done.filter(s => localDateStr(s.startedAt) === dateStr);
    const life = sessionsOnDay.length ? 'completed' : (ts < todayStart.getTime() ? 'past' : day ? 'planned' : 'rest');
    return { dn, ts, dateStr, day, life, isToday: dateStr === todayStr, sessions: sessionsOnDay };
  });
  const tmsMissing = program ? missingTMs(program) : [];
  const doneToday = done.filter(s => localDateStr(s.startedAt) === todayStr);

  // Week stats
  const thisWeek = done.filter(s => s.startedAt >= ws);
  const lastWeek = done.filter(s => s.startedAt >= ws - 7 * DAY && s.startedAt < ws);
  const vol = (ss) => ss.reduce((a, s) => a + (s.sets || []).filter(x => x.type !== 'warmup').reduce((b, x) => b + (x.w || 0) * (x.reps || 0), 0), 0);
  const volThis = vol(thisWeek), volLast = vol(lastWeek);
  // Compare against last week up to the same weekday so a partial week isn't "down 60%".
  const elapsedFrac = Math.min(1, (Date.now() - ws) / (7 * DAY));
  const volLastSoFar = vol(lastWeek.filter(s => s.startedAt - (ws - 7 * DAY) <= elapsedFrac * 7 * DAY));
  const plannedPerWeek = program ? program.days.filter(d => d.day).length || program.days.length : 0;
  const streak = weeklyStreak(state, 1);

  // Explicit skip: the plan moves on and the day is recorded as skipped (never silently).
  const skipLink = nextDay ? (
    <button onClick={() => { actions.updateProgram(program.id, { skips: skipProgramDay(program, nextDay.id).skips }); toast(`${nextDay.name} skipped this week`); }} style={heroLink(LL.fgMute)}>Skip {nextDay.name}</button>
  ) : null;

  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening';
  const name = (state.user.name || '').split(' ')[0];

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: LL.bg, paddingBottom: live ? 160 : 100 }}>
      <DashStyles />
      <div className="ll-wrap">
      {/* Header */}
      <PageHeader back={false} style={{ paddingBottom: 6 }}
        kicker={now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        title={`${greeting}${name ? `, ${name}` : ''}`}
        titleStyle={{ whiteSpace: 'normal', fontSize: `clamp(${LL.fs.h2}px, 8.6vw, ${LL.fs.h1}px)` /* token-ok: fluid between h2 and h1 */, textWrap: 'balance' }}
        right={streak > 0 ? (
          <div title="Weeks in a row with a workout" aria-label={`${streak} week streak`} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 12px', borderRadius: LL.r.md, background: LL.accentSoft, color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            <LLIcon.flame style={{ width: 16, height: 16 }} /> {streak} wk
          </div>
        ) : null} />

      {/* Desktop (≥ 900px): two columns — today and the plan on the left, the
          read, coach and history on the right. Phone: one column in the order
          below (each section carries its phone position as a flex order). */}
      <div className="ll-dash">
      <div className="ll-col">
      <div style={{ order: 1 }}>
      {/* Stale unfinished workout */}
      {stale && (
        <div style={{ ...card, margin: '12px 16px 0', padding: 14, borderColor: `color-mix(in oklab, ${LL.warn} 50%, ${LL.line})` }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>Unfinished: {live.name || 'Workout'} from {relDay(live.startedAt)}</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 4 }}>{plural(live.sets.length, 'set')} logged. Save it (it ends at its last set), or discard it.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => { actions.updateSession(live.id, { rest: null }); actions.completeSession(live.id, { completedAt: staleEndAt(live) }); }} disabled={!live.sets.length} style={btn('accent', { flex: 1 })}>Save it</button>
            <button onClick={() => actions.cancelSession(live.id)} style={btn('ghost', { flex: 1 })}>Discard</button>
          </div>
        </div>
      )}
      </div>
      <div style={{ order: 2 }}>
      {/* Today hero */}
      <div style={{ padding: '14px 16px 0' }}>
        {!program ? (
          <HeroCard title="Let's set you up" sub="Build today's workout from your equipment and time, pick a program, or just start lifting.">
            <button onClick={() => setGenOpen(true)} style={btn('accent', { flex: '1.4 1 0', whiteSpace: 'nowrap', padding: '0 12px' })}>Build a workout</button>
            <button onClick={() => go('programs')} style={btn('ghost', { flex: '1 1 0', whiteSpace: 'nowrap', padding: '0 12px' })}>Programs</button>
          </HeroCard>
        ) : live && !stale ? (
          <LiveHero live={live} onResume={() => go('active-workout', { dayId: live.dayId || undefined })} />
        ) : doneToday.length ? (
          <HeroCard eyebrow="Done today" title={doneToday[doneToday.length - 1].name || 'Workout'}
            sub={`${plural(doneToday[doneToday.length - 1].sets.filter(x => x.type !== 'warmup').length, 'set')} · ${minsLabel(doneToday[doneToday.length - 1].completedAt - doneToday[doneToday.length - 1].startedAt)}${nextDay ? ` · Next: ${nextDay.name}${nx.inDays === 1 ? ' tomorrow' : nx.inDays > 1 ? ` on ${nx.plannedFor}` : ''}` : ''}`}>
            <button onClick={() => go('workout-review', { sessionId: doneToday[doneToday.length - 1].id })} style={btn('ghost', { flex: 1 })}>Review</button>
            <button onClick={startEmpty} style={btn('ghost', { flex: 1 })}>Another workout</button>
          </HeroCard>
        ) : !nextDay ? (
          <HeroCard eyebrow={program.name} title="No days in this program yet" sub="Add training days to your program, or build a one-off workout for today.">
            <button onClick={() => go('program-edit', { programId: program.id })} style={btn('accent', { flex: 1 })}>Edit program</button>
            <button onClick={() => setGenOpen(true)} style={btn('ghost', { flex: 1 })}>Build a workout</button>
          </HeroCard>
        ) : !done.some(s => s.programId === program.id) ? (
          // A new program always starts at its first day, whatever weekday it is.
          <HeroCard tone="accent" eyebrow={`Start ${program.name}`} title={nextDay.name}
            sub={`Your first workout · ${nextDay.exercises.length} exercises · ~${estMinutes(nextDay, program)} min. Lifts without history start with finding a working weight.`}>
            <button data-ll-primary onClick={() => startDay(nextDay)} style={btn('ink', { flex: 1 })}><LLIcon.play style={{ width: 18, height: 18 }} /> Start first workout</button>
          </HeroCard>
        ) : !restDay ? (
          <>
          <HeroCard tone="accent" eyebrow={`${nx.today ? 'Today' : 'Up next'} · ${weekText}`} title={nextDay.name}
            sub={`${nextDay.exercises.length} exercises · ~${estMinutes(nextDay, program)} min · ${[...new Set(nextDay.exercises.map(e => e.group))].slice(0, 4).join(', ')}`}
            note={rotationLine}>
            <button data-ll-primary onClick={() => startDay(nextDay)} style={btn('ink', { flex: 1 })}><LLIcon.play style={{ width: 18, height: 18 }} /> Start workout</button>
            <button aria-label={`Preview ${nextDay.name}`} onClick={() => setDaySheet({ day: nextDay, date: todayStr })} style={btn('inkGhost', { width: 52, padding: 0 })}><LLIcon.more /></button>
          </HeroCard>
          {program.days.length > 1 && <HeroLinks>{skipLink}</HeroLinks>}
          </>
        ) : (
          <>
          <HeroCard eyebrow={`Rest day · ${weekText}`} title={`Next: ${nextDay.name}`}
            sub={`${nx.inDays === 1 ? 'Tomorrow' : `Planned for ${nx.plannedFor}`} · ${nextDay.exercises.length} exercises. Recovery is when you grow — or train now if your schedule moved.`}
            note={rotationLine.replace(/^Planned for \w+( · )?/, '')}>
            <button onClick={() => startDay(nextDay)} style={btn('accent', { flex: 1 })}>Start {nextDay.name} now</button>
          </HeroCard>
          <HeroLinks>
            <button onClick={startEmpty} style={heroLink(LL.accentText)}>Start an empty workout</button>
            {program.days.length > 1 && skipLink}
          </HeroLinks>
          </>
        )}
      </div>
      </div>
      <div style={{ order: 3 }}>
      {/* At most one system banner, below the hero. Training maxes (the plan
          can't prescribe loads without them) outrank the backup nudge. */}
      {program && tmsMissing.length > 0 ? (
        <Banner tone="warn" icon={LLIcon.target}
          title={`Set ${simple ? 'working' : 'training'} maxes for ${tmsMissing.length === 1 ? tmsMissing[0].name || 'a lift' : `${tmsMissing.length} lifts`}`}
          onClick={() => go('program-edit', { programId: program.id, setTMs: true })} />
      ) : nudge && !stale ? (
        <Banner tone="info" icon={LLIcon.cloud} title="Back up your training"
          onClick={() => go('settings/sync')}
          onDismiss={() => { dismissBackupNudge(); setNudge(false); }} />
      ) : null}
      </div>
      <div style={{ order: 4 }}>
      {/* Build today's workout (generator.js): any day, with or without a program */}
      {program && !(live && !stale) && <BuildWorkoutCard onOpen={() => setGenOpen(true)} />}
      </div>
      <div style={{ order: 5 }}>
      {/* Week strip */}
      {program && (
        <div style={{ ...card, margin: '12px 16px 0', padding: '12px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px 10px' }}>
            <div style={kicker}>This week</div>
            <div style={{ marginLeft: 'auto', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' }}>
              {thisWeek.length}/{plannedPerWeek} workouts
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {weekDays.map(w => {
              const filled = w.life === 'completed';
              const past = w.life === 'past';
              const planned = !!w.day && !past;
              return (
                <button key={w.dn} onClick={() => {
                  if (w.sessions.length) go('workout-review', { sessionId: w.sessions[w.sessions.length - 1].id });
                  else if (w.day) setDaySheet({ day: w.day, date: w.dateStr });
                }} style={{ background: 'none', border: 'none', padding: 0, cursor: w.day || w.sessions.length ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: w.isToday ? LL.accentText : LL.fgDim }}>{w.dn.slice(0, 1)}</span>
                  <span style={{
                    width: 36, height: 36, borderRadius: LL.r.pill, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: filled ? LL.accent : planned ? LL.surface2 : 'transparent',
                    border: w.isToday && !filled ? `2px solid ${LL.accent}` : planned && !filled ? `1px solid ${LL.line2}` : past && w.day ? `1px dashed ${LL.line}` : '1px solid transparent',
                    color: filled ? LL.accentInk : past ? LL.fgDim : LL.fgMute,
                    fontFamily: LL.display, fontSize: LL.fs.body, fontWeight: 700,
                  }}>
                    {filled ? <LLIcon.check style={{ width: 18, height: 18 }} /> : new Date(w.ts).getDate()}
                  </span>
                </button>
              );
            })}
          </div>
          {thisWeek.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '12px 4px 0', paddingTop: 10, borderTop: `1px solid ${LL.line}`, fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: LL.fg, fontWeight: 600 }}>{fmtVol(volThis, units)}</span>
              <span>· {plural(thisWeek.reduce((a, s) => a + (s.sets || []).filter(x => x.type !== 'warmup').length, 0), 'set')}</span>
              {volLastSoFar > 0 && <span style={{ marginLeft: 'auto', color: volThis >= volLastSoFar ? LL.accentText : LL.fgMute, fontWeight: 600 }}>{deltaStr(volThis, volLastSoFar)}</span>}
            </div>
          )}
        </div>
      )}
      </div>
      <div style={{ order: 9 }}>
      {/* Program days */}
      {program && (
        <div style={{ margin: '18px 16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8, padding: '0 2px' }}>
            <div style={kicker}>{program.name}</div>
            <button onClick={() => go('programs')} style={{ marginLeft: 'auto', minHeight: 44, margin: '-14px -10px -14px auto', padding: '0 10px', background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' }}>Programs</button>
          </div>
          <div style={{ ...card, overflow: 'hidden' }}>
            {program.days.map((d, i) => {
              const isNext = nextDay?.id === d.id;
              const doneNow = doneIds.has(d.id);
              const liveNow = live && !stale && live.dayId === d.id;
              const last = [...done].reverse().find(s => s.dayId === d.id);
              return (
                <button key={d.id} onClick={() => setDaySheet({ day: d, date: todayStr })} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'none', border: 'none',
                  borderTop: i ? `1px solid ${LL.line}` : 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                  <span style={{ width: 42, height: 42, borderRadius: LL.r.md, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: doneNow ? LL.accent : LL.surface2, color: doneNow ? LL.accentInk : isNext ? LL.accentText : LL.fgMute,
                    border: isNext && !doneNow ? `1.5px solid ${LL.accent}` : 'none',
                    fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 700 }}>
                    {doneNow ? <LLIcon.check style={{ width: 18, height: 18 }} /> : (d.day || `D${i + 1}`)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{d.name}</div>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2 }}>
                      {d.exercises.length} exercises · ~{estMinutes(d, program)} min{last ? ` · last ${relDay(last.startedAt)}` : ''}
                    </div>
                  </span>
                  {liveNow ? <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.accentText, fontWeight: 700 }}>Live</span>
                    : isNext ? <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.accentText, fontWeight: 700 }}>Next</span>
                    : missedIds.has(d.id) && !doneNow ? <span title="The rotation moved past this day last week without it" style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontWeight: 600, textAlign: 'right' }}>Not done last week</span> : null}
                  <LLIcon.chev style={{ color: LL.fgFaint }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
      <div style={{ order: 11 }}>
      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '12px 16px 0' }}>
        <button onClick={startEmpty} style={{ ...card, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' }}>
          <LLIcon.plus style={{ width: 18, height: 18 }} /> Empty workout
        </button>
        <button onClick={() => go('settings/body')} style={{ ...card, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' }}>
          <LLIcon.body style={{ width: 18, height: 18 }} /> Log body weight
        </button>
      </div>
      </div>
      </div>
      <div className="ll-col">
      <div style={{ order: 6 }}>
      {/* Today's read: readiness + up to three insights in one grouped card */}
      {(ready.score != null || insights.length > 0) && (
        <div style={{ margin: '20px 16px 0' }}>
          <div style={{ ...kicker, padding: '0 2px', marginBottom: 8 }}>Today's read</div>
          <div style={{ ...card, overflow: 'hidden' }}>
            {ready.score != null && (
              <button onClick={() => go('progress')} style={{ width: '100%', padding: 14, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', textAlign: 'left', background: 'none', border: 'none' }}>
                <Ring value={ready.score} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute }}>Readiness</div>
                  <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, marginTop: 1 }}>{ready.label}</div>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 3, lineHeight: 1.4 }}>{ready.reasons[0]}</div>
                </span>
              </button>
            )}
            {insights.map((ins, i) => (
              <div key={i} style={{ padding: '12px 14px', borderTop: (i || ready.score != null) ? `1px solid ${LL.line}` : 'none', display: 'flex', gap: 12 }}>
                <span style={{ width: 32, height: 32, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: ins.tone === 'warn' ? `color-mix(in oklab, ${LL.warn} 18%, transparent)` : ins.tone === 'good' ? `color-mix(in oklab, ${LL.pr} 16%, transparent)` : `color-mix(in oklab, ${LL.info} 16%, transparent)`,
                  color: ins.tone === 'warn' ? LL.warn : ins.tone === 'good' ? LL.prText : LL.info }}>
                  {React.createElement(LLIcon[ins.icon] || LLIcon.ai, { style: { width: 18, height: 18 } })}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>{ins.title}</div>
                  <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 3, lineHeight: 1.45 }}>{ins.body}</div>
                  {ins.action && (
                    <button onClick={() => ins.action.deload ? (program && setDeloadAsk(ins)) : go(ins.action.route, ins.action.params || {})} style={{ ...linkBtn, minHeight: 44, margin: '-2px 0 -12px', paddingRight: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {ins.action.route === 'ai-coach' && <LLIcon.ai style={{ width: 14, height: 14 }} />}{ins.action.label}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      <div style={{ order: 7 }}>
      <WeeklyReviewCard style={{ margin: '12px 16px 0' }} />
      </div>
      <div style={{ order: 8 }}>
      {/* Coach entry */}
      <button onClick={() => go('ai-coach')} style={{ ...card, margin: '12px 16px 0', width: 'calc(100% - 32px)', padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left',
        background: `linear-gradient(135deg, color-mix(in oklab, ${LL.accent} 12%, ${LL.surface}), ${LL.surface})` }}>
        <span style={{ width: 40, height: 40, borderRadius: LL.r.md, background: LL.accent, color: LL.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LLIcon.ai style={{ width: 22, height: 22 }} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>Ask your coach</div>
          <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2 }}>
            {aiMode === 'byo' ? 'Answers from your real training data · your key' : aiMode === 'relay' ? 'Free · answers from your real training data' : 'Not connected right now · tap for options'}
          </div>
        </span>
        <LLIcon.chev style={{ color: LL.fgFaint }} />
      </button>
      </div>
      <div style={{ order: 10 }}>
      {/* Recent workouts */}
      {done.length > 0 && (
        <div style={{ margin: '18px 16px 0' }}>
          <div style={{ ...kicker, padding: '0 2px', marginBottom: 8 }}>Recent</div>
          <div style={{ ...card, overflow: 'hidden' }}>
            {[...done].reverse().slice(0, 4).map((s, i) => {
              const v = (s.sets || []).filter(x => x.type !== 'warmup').reduce((a, x) => a + (x.w || 0) * (x.reps || 0), 0);
              const prs = (s.prsHit || []).length;
              return (
                <button key={s.id} onClick={() => go('workout-review', { sessionId: s.id })} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'none', border: 'none', borderTop: i ? `1px solid ${LL.line}` : 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{s.name || state.programs.find(p => p.id === s.programId)?.days.find(d => d.id === s.dayId)?.name || 'Workout'}</div>
                    <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                      {relDay(s.startedAt)} · {minsLabel(s.completedAt - s.startedAt)}{v > 0 ? ` · ${fmtVol(v, units)}` : ''}
                    </div>
                  </span>
                  {prs > 0 && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 800, color: LL.prText, background: `color-mix(in oklab, ${LL.pr} 16%, transparent)`, padding: '3px 7px', borderRadius: LL.r.sm }}>{prs} PR{prs > 1 ? 's' : ''}</span>}
                  <LLIcon.chev style={{ color: LL.fgFaint }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
      </div>
      </div>
      </div>

      {/* Day sheet */}
      <LLSheet open={!!daySheet} onClose={() => setDaySheet(null)} title={daySheet?.day?.name || ''} subtitle={daySheet?.day?.day ? `${daySheet.day.day} · ~${estMinutes(daySheet.day, program)} min` : ''} maxHeight="85%">
        {daySheet && program && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: LL.r.md, overflow: 'hidden', border: `1px solid ${LL.line}` }}>
              {daySheet.day.exercises.map((pe, i) => {
                const rx = prescriptionFor(program, pe);
                const h = exerciseHistory(state, pe.exerciseId);
                const last = h[h.length - 1];
                return (
                  <div key={pe.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderTop: i ? `1px solid ${LL.line}` : 'none', background: LL.surface2 }}>
                    <span style={{ width: 22, fontFamily: LL.display, fontSize: LL.fs.body, fontWeight: 700, color: LL.fgDim }}>{i + 1}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{rx.name || pe.name}</div>
                      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                        {rx.sets} × {rx.repMin}–{rx.repMax}{pe.targetWeight ? ` · ${fmtWeight(pe.targetWeight, units)} ${unitLabel(units)}` : ''}{last ? ` · last ${fmtWeight(last.bestW, units)}×${last.bestReps}` : ''}
                      </div>
                    </span>
                  </div>
                );
              })}
            </div>
            <button onClick={() => startDay(daySheet.day)} style={{ ...btn('accent'), width: '100%', marginTop: 14, height: 54 }}>
              <LLIcon.play style={{ width: 18, height: 18 }} /> Start {daySheet.day.name}
            </button>
            <button onClick={() => { setDaySheet(null); go('program-edit', { programId: program.id }); }} style={{ ...btn('ghost'), width: '100%', marginTop: 8 }}>Edit in program</button>
          </div>
        )}
      </LLSheet>

      <GenerateWorkoutSheet open={genOpen} onClose={() => setGenOpen(false)} />

      {/* Deload confirm: it changes this week's program, so it is never a one-tap link */}
      <LLSheet open={!!deloadAsk} onClose={() => setDeloadAsk(null)} title="Start a deload week?" subtitle={program ? program.name : ''}>
        {deloadAsk && program && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fgMute, lineHeight: 1.5, marginBottom: 8 }}>
              Until Sunday your workouts are about 10% lighter with roughly 40% fewer working sets. Next week goes back to normal on its own.
            </div>
            <button onClick={() => { actions.setDeloadWeek(program.id, true); setDeloadAsk(null); }} style={btn('accent', { width: '100%' })}>Start deload week</button>
            <button onClick={() => setDeloadAsk(null)} style={btn('ghost', { width: '100%' })}>Not now</button>
          </div>
        )}
      </LLSheet>

      {/* Another workout is live (or a stale one from an earlier day) */}
      <LLSheet open={!!guard} onClose={() => setGuard(null)} title={guard?.stale ? 'Finish last workout first?' : 'Finish your current workout first?'} subtitle={live ? `${live.name || 'Workout'} · ${plural(live.sets.length, 'set')}${guard?.stale ? ' · ' + relDay(live.startedAt) : ''}` : ''}>
        {guard && live && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {guard.stale ? (
              <>
                {live.sets.length > 0 && <button onClick={() => {
                  const d = guard.day; actions.updateSession(live.id, { rest: null }); actions.completeSession(live.id, { completedAt: staleEndAt(live) }); setGuard(null); setDaySheet(null);
                  go('active-workout', d ? { dayId: d.id } : { freestyle: 'Workout' });
                }} style={btn('accent', { width: '100%' })}>Save it, then start {guard.day ? guard.day.name : 'a new workout'}</button>}
                <button onClick={() => { const d = guard.day; actions.cancelSession(live.id, { silent: true }); setGuard(null); setDaySheet(null); go('active-workout', d ? { dayId: d.id } : { freestyle: 'Workout' }); }} style={{ ...btn('ghost', { width: '100%' }), color: LL.danger }}>Discard it and start {guard.day ? guard.day.name : 'fresh'}</button>
              </>
            ) : (
              <>
                <button onClick={() => { setGuard(null); go('active-workout', { dayId: live.dayId || undefined }); }} style={btn('accent', { width: '100%' })}>Resume {live.name || 'workout'}</button>
                <button onClick={() => { const d = guard.day; actions.cancelSession(live.id, { silent: true }); setGuard(null); setDaySheet(null); go('active-workout', { dayId: d.id }); }} style={{ ...btn('ghost', { width: '100%' }), color: LL.danger }}>Discard it and start {guard.day.name}</button>
              </>
            )}
          </div>
        )}
      </LLSheet>
    </div>
  );
}

function fmtVol(lb, units) {
  const v = units === 'kg' ? lb * 0.45359237 : lb;
  return `${fmtCompact(v)} ${unitLabel(units)}`;
}
function deltaStr(a, b) {
  const p = Math.round(((a - b) / b) * 100);
  return `${p >= 0 ? '+' : '−'}${Math.abs(p)}% vs last wk`;
}

// One-line system banner (56px): icon tile, 15/600 title, chevron, optional ×.
function Banner({ icon, title, sub, tone = 'info', onClick, onDismiss }) {
  const c = tone === 'warn' ? LL.warn : LL.info;
  return (
    <div style={{ ...card, margin: '12px 16px 0', height: 56, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <button onClick={onClick} style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '0 6px 0 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ width: 32, height: 32, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in oklab, ${c} 16%, transparent)`, color: c }}>{React.createElement(icon, { style: { width: 18, height: 18 } })}</span>
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{title}</span>
          {sub && <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute }}> · {sub}</span>}
        </span>
        <LLIcon.chev style={{ color: LL.fgFaint, flexShrink: 0 }} />
      </button>
      {onDismiss && (
        <button aria-label="Dismiss" onClick={onDismiss} style={{ width: 44, height: '100%', flexShrink: 0, background: 'none', border: 'none', borderLeft: `1px solid ${LL.line}`, color: LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <LLIcon.x style={{ width: 16, height: 16 }} />
        </button>
      )}
    </div>
  );
}

const linkBtn = { padding: 0, background: 'none', border: 'none', color: LL.accentText, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 700, cursor: 'pointer' };

function btn(kind, extra = {}) {
  const base = {
    height: 50, borderRadius: LL.r.md, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, padding: '0 16px', border: 'none',
  };
  const kinds = {
    accent: { background: LL.accent, color: LL.accentInk },
    ink: { background: LL.accentInk, color: LL.accent },
    inkGhost: { background: 'rgba(0,0,0,0.14)', color: LL.accentInk },
    ghost: { background: LL.surface2, color: LL.fg, border: `1px solid ${LL.line2}` },
  };
  return { ...base, ...kinds[kind], ...extra };
}

function HeroLinks({ children }) {
  return <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>{children}</div>;
}
const heroLink = (color) => ({ minHeight: 36, padding: '6px 12px', background: 'none', border: 'none', color, fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer' });

// In-progress hero with the running rest countdown (ticks only while resting).
function LiveHero({ live, onResume }) {
  const resting = !!(live.rest && live.rest.endsAt > Date.now());
  const [now, setNow] = React.useState(Date.now());
  const endsAt = live.rest && live.rest.endsAt;
  React.useEffect(() => {
    if (!resting) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [resting, endsAt]);
  const left = resting ? Math.ceil((endsAt - now) / 1000) : 0;
  const elapsed = Date.now() - live.startedAt;
  const working = live.sets.filter(x => x.type !== 'warmup').length;
  return (
    <HeroCard tone="live" eyebrow="In progress" title={live.name || 'Workout'}
      sub={`Started ${minsLabel(elapsed) === '<1 min' ? 'just now' : minsLabel(elapsed) + ' ago'} · ${plural(working, 'set')} logged`}>
      <button onClick={onResume} style={btn('accent', { flex: 1 })}><LLIcon.play style={{ width: 18, height: 18 }} /> Resume</button>
      {left > 0 && (
        <div role="timer" aria-label={`Rest ${clock(left)} left`} style={{ height: 50, padding: '0 14px', borderRadius: LL.r.md, background: LL.surface2, border: `1px solid ${LL.line2}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1, color: LL.fgDim, textTransform: 'uppercase' }}>Rest</span>
          <span style={{ fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: left <= 10 ? LL.accentText : LL.fg, fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>{clock(left)}</span>
        </div>
      )}
    </HeroCard>
  );
}

function HeroCard({ tone, eyebrow, title, sub, note, children }) {
  const accent = tone === 'accent';
  return (
    <div style={{
      padding: 16, borderRadius: LL.r.lg, position: 'relative', overflow: 'hidden',
      background: accent ? LL.accent : LL.surface,
      border: accent ? 'none' : tone === 'live' ? `1.5px solid ${LL.accent}` : `1px solid ${LL.line}`,
      color: accent ? LL.accentInk : LL.fg,
    }}>
      {eyebrow && <div style={{ ...kicker, color: accent ? LL.accentInk : tone === 'live' ? LL.accentText : LL.fgDim, opacity: accent ? 0.7 : 1 }}>{eyebrow}</div>}
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.05, marginTop: 4 }}>{title}</div>
      {sub && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, marginTop: 6, lineHeight: 1.45, color: accent ? LL.accentInk : LL.fgMute, opacity: accent ? 0.8 : 1 }}>{sub}</div>}
      {note && <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, marginTop: 4, lineHeight: 1.4, color: accent ? LL.accentInk : LL.fgDim, opacity: accent ? 0.7 : 1 }}>{note}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>{children}</div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div style={{ ...card, padding: '12px 12px' }}>
      <div style={kicker}>{label}</div>
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.stat, fontWeight: 700, color: LL.fg, marginTop: 4, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, color: tone === 'up' ? LL.accentText : tone === 'down' ? LL.warn : LL.fgDim, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
    </div>
  );
}

function Ring({ value, size = 56 }) {
  const r = size / 2 - 5, c = 2 * Math.PI * r;
  const color = value >= 80 ? LL.accent : value >= 65 ? LL.accent : value >= 45 ? LL.warn : LL.danger;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={LL.surface3} strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .8s ' + LL.ease }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: LL.display, fontSize: LL.fs.lg, fontWeight: 700, color: LL.fg }}>{value}</div>
    </div>
  );
}
